import { Widget } from "blue-widgets";

class Pagination extends Widget {
  constructor(el) {
    super(el);
    this.previousClickHandler = this.previousClickHandler.bind(this);
    this.nextClickHandler = this.nextClickHandler.bind(this);
    this.init();
  }

  createArrowElement = (currentPage, pages, direction) => {
    // Create the <li> element
    const liElement = document.createElement("li");
    liElement.classList.add("pagination__arrow");

    // Create the <button> element
    const buttonElement = document.createElement("button");

    if (direction === "left") {
      buttonElement.setAttribute("aria-label", "Previous");

      // Check if currentPage is 0 and add the "disabled" attribute
      if (currentPage === 1) {
        buttonElement.setAttribute("disabled", "true");
      }

      // Add click event
      buttonElement.addEventListener("click", this.previousClickHandler);

      // Create the <i> element with classes and aria-hidden attribute
      const iElement = document.createElement("i");
      iElement.classList.add("icon", "icon-arrow-left");
      iElement.setAttribute("aria-hidden", "true");

      // Append the <i> element to the <button> element
      buttonElement.appendChild(iElement);
    } else if (direction === "right") {
      buttonElement.setAttribute("aria-label", "Next");

      // Check if currentPage is equal to (pages - 1) and add the "disabled" attribute
      if (currentPage === pages) {
        buttonElement.setAttribute("disabled", "true");
      }

      // Create the <i> element with classes and aria-hidden attribute
      const iElement = document.createElement("i");
      iElement.classList.add("icon", "icon-arrow-right");
      iElement.setAttribute("aria-hidden", "true");

      // Add click event
      buttonElement.addEventListener("click", this.nextClickHandler);

      // Append the <i> element to the <button> element
      buttonElement.appendChild(iElement);
    }

    // Append the <button> element to the <li> element
    liElement.appendChild(buttonElement);

    return liElement;
  };

  createBulletElement = (text) => {
    // Create the <li> element
    const liElement = document.createElement("li");
    liElement.classList.add("pagination__bullet");
    liElement.setAttribute("data-bullet", "");

    // Create the <button> element
    const buttonElement = document.createElement("button");
    buttonElement.setAttribute("aria-label", `Go to page ${text}`);
    buttonElement.setAttribute("data-value", text);

    // Set the button text to the specified text
    buttonElement.textContent = text;

    // Add click event
    buttonElement.addEventListener("click", () => {
      this.bulletClickHandler(text);
    });

    // Append the <button> element to the <li> element
    liElement.appendChild(buttonElement);

    return liElement;
  };

  createMoreElement = () => {
    // Create the <li> element
    const liElement = document.createElement("li");
    liElement.classList.add("pagination__more");

    // Create the <div> element
    const divElement = document.createElement("div");
    divElement.textContent = "...";

    // Append the <div> element to the <li> element
    liElement.appendChild(divElement);

    return liElement;
  };

  buildMarkupHandler(pages, currentPage) {
    this.el.innerHTML = "";
    if (pages <= 1) {
      return;
    }
    const ulElement = document.createElement("ul");
    ulElement.setAttribute("data-bullet-list", "");

    const loopLength = pages <= 7 ? pages : 7;
    for (let index = 0; index < loopLength; index++) {
      ulElement.appendChild(this.createBulletElement(index + 1));
    }

    if (pages > 1) {
      ulElement.prepend(this.createArrowElement(currentPage, pages, "left"));
      ulElement.appendChild(
        this.createArrowElement(currentPage, pages, "right")
      );
    }

    this.el.appendChild(ulElement);
    this.listRendererHandler();
  }

  observer = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.attributeName === "data-pages") {
        this.currentPage = 1;
        this.pages = this.el.getAttribute("data-pages") || 1;
        this.buildMarkupHandler(this.pages, this.currentPage);
        this.setCurrentHandler();
      }
      if (mutation.attributeName === "data-current-page") {
        this.currentPage =
          parseInt(this.el.getAttribute("data-current-page")) || 1;
        this.setCurrentHandler();
        this.toggleArrowStateHandler();
      }
    });
  });

  nextClickHandler() {
    this.currentPage =
      this.currentPage + 1 <= this.pages ? this.currentPage + 1 : this.pages;
    this.valueChangeHandler();
    this.scrollToTop();
  }

  previousClickHandler() {
    this.currentPage = this.currentPage - 1 >= 0 ? this.currentPage - 1 : 0;
    this.valueChangeHandler();
    this.scrollToTop();
  }

  bulletClickHandler(value) {
    this.currentPage = parseInt(value);
    this.valueChangeHandler();
    this.scrollToTop();
  }

  setCurrentHandler() {
    const previous = this.el.querySelector("[aria-current='true']");
    if (previous) {
      previous.removeAttribute("aria-current");
    }

    const next = this.el.querySelector(`[data-value='${this.currentPage}']`);
    if (next) {
      next.setAttribute("aria-current", "true");
    }
  }

  toggleArrowStateHandler() {
    if (parseInt(this.pages) === 1) {
      return;
    }

    const previousArrow = this.el.querySelector("[aria-label='Previous']");
    const nextArrow = this.el.querySelector("[aria-label='Next']");

    if (this.currentPage === 1) {
      previousArrow.setAttribute("disabled", "true");
    } else {
      previousArrow.removeAttribute("disabled");
    }

    if (parseInt(this.currentPage) === parseInt(this.pages)) {
      nextArrow.setAttribute("disabled", "true");
    } else {
      nextArrow.removeAttribute("disabled");
    }
  }

  replaceChildrenHandler(newNodes, oldNodes) {
    oldNodes.forEach((node, index) => {
      const parent = node.parentNode;
      parent.replaceChild(newNodes[index], node);
    });
  }

  listRendererHandler() {
    if (this.pages <= 7) {
      return;
    }
    const list = this.el.querySelector("[data-bullet-list]");
    if (list && this.currentPage) {
      const bullets = list.querySelectorAll("li");

      // Next dots
      if (this.pages - this.currentPage > 3) {
        this.replaceChildrenHandler(
          [this.createMoreElement()],
          [bullets[bullets.length - 3]]
        );
        this.replaceChildrenHandler(
          [this.createBulletElement(this.pages)],
          [bullets[bullets.length - 2]]
        );
      }

      // Next dots revert
      if (
        this.currentPage >= this.pages - 3 &&
        bullets[bullets.length - 3].classList.contains("pagination__more")
      ) {
        this.replaceChildrenHandler(
          [
            this.createBulletElement(this.pages - 1),
            this.createBulletElement(this.pages - 2),
            this.createBulletElement(this.pages - 3),
            this.createBulletElement(this.pages - 4)
          ],
          [
            bullets[bullets.length - 3],
            bullets[bullets.length - 4],
            bullets[bullets.length - 5],
            bullets[bullets.length - 6]
          ]
        );
      }

      // Previous dots
      if (
        this.currentPage > 3 &&
        !bullets[2].classList.contains("pagination__more")
      ) {
        this.replaceChildrenHandler([this.createMoreElement()], [bullets[2]]);
      }

      // Previous dots revert
      if (
        this.currentPage <= 3 &&
        bullets[2].classList.contains("pagination__more")
      ) {
        this.replaceChildrenHandler(
          [this.createBulletElement(2)],
          [bullets[2]]
        );
      }

      // Shift central dots
      if (this.currentPage > 3 && this.pages - this.currentPage > 3) {
        this.replaceChildrenHandler(
          [
            this.createBulletElement(this.currentPage - 1),
            this.createBulletElement(this.currentPage),
            this.createBulletElement(this.currentPage + 1)
          ],
          [bullets[3], bullets[4], bullets[5]]
        );
      }
    }
  }

  valueChangeHandler() {
    this.listRendererHandler();
    this.setCurrentHandler();
    this.toggleArrowStateHandler();
    this.emitChangeHandler();
  }

  emitChangeHandler() {
    const customEvent = new CustomEvent("pagination-value-changed", {
      detail: {
        value: this.currentPage
      }
    });
    this.el.dispatchEvent(customEvent);
  }

  init() {
    this.currentPage = this.options.currentPage || 1;
    this.pages = this.options.pages || 1;

    if (this.pages > 1) {
      this.buildMarkupHandler(this.pages, this.currentPage);
      this.valueChangeHandler();
    }

    const config = { attributes: true };
    this.observer.observe(this.el, config);

    // Wait for layout shift to finish
    window.onload = () => this.setScrollOffset();
  }

  setScrollOffset = () => {
    let parentContainer = document.querySelector("[data-widget='FilterBlock']");
    if (!parentContainer) {
      parentContainer = document.querySelector(
        "[data-widget='ContentListing']"
      );
    }
    if (!parentContainer) {
      parentContainer = document.body;
    }

    const rootStyles = getComputedStyle(document.documentElement);
    const headerHeight = rootStyles.getPropertyValue("--header-height");
    const headerHeightInteger = parseInt(headerHeight, 10);

    this.scrollOffset =
      parentContainer.getBoundingClientRect().top +
      window.scrollY -
      headerHeightInteger;
  };

  scrollToTop = () => {
    window.scrollTo(0, this.scrollOffset);
  };

  beforeRemove() {
    const arrows = this.el.querySelectorAll("li.pagination__arrow button");
    const bullets = this.el.querySelectorAll("li.pagination__bullet button");
    arrows.forEach((button) => {
      button.removeEventListener("click", this.nextClickHandler);
    });
    bullets.forEach((button) => {
      button.removeEventListener("click", this.nextClickHandler);
    });
  }
}

export default Pagination;
